// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module TopPagesTable
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS top-pages table used by analytics
 * dashboards to compare the most-visited routes. The component includes
 * loading and empty states while preserving the standalone CSS class contract.
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
  Skeleton,
} from "@geenius/ui/react-css";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/** Summary row rendered by the React CSS top-pages table. */
export interface TopPageSummary {
  url: string;
  views: number;
  uniqueVisitors?: number;
  averageTimeMs?: number;
  averageTimeSeconds?: number;
  averageTime?: string;
}

function normalizeCount(value: number | undefined): number | undefined {
  return Number.isFinite(value) ? Math.max(0, value ?? 0) : undefined;
}

function formatDuration(page: TopPageSummary): string {
  if (page.averageTime) return page.averageTime;

  const milliseconds =
    page.averageTimeMs ??
    (page.averageTimeSeconds !== undefined
      ? page.averageTimeSeconds * 1_000
      : undefined);
  if (!Number.isFinite(milliseconds)) return "—";

  const totalSeconds = Math.max(0, Math.round((milliseconds ?? 0) / 1_000));
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/**
 * Props for rendering the React CSS top-pages table.
 *
 * @property pages Ordered page summary rows with visit and engagement counts.
 * @property isLoading Whether the table should render placeholder rows.
 * @property className Optional class name merged into the table wrapper.
 */
export interface TopPagesTableProps {
  pages: TopPageSummary[];
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React CSS top-pages table with proportional view bars.
 *
 * @param props Table props containing page rows and optional loading state.
 * @returns A top-pages table element for analytics dashboards.
 */
export function TopPagesTable({
  error,
  pages,
  isLoading,
  className,
  onRetry,
}: TopPagesTableProps): React.ReactElement {
  const safePages = pages.map((page) => ({
    ...page,
    uniqueVisitors: normalizeCount(page.uniqueVisitors),
    views: Number.isFinite(page.views) ? Math.max(0, page.views) : 0,
  }));
  const maxViews =
    safePages.length > 0
      ? Math.max(...safePages.map((page) => page.views), 1)
      : 1;
  const copy = useAnalyticsCopy();

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle
            className="gn-analytics-top-pages__title"
            data-analytics-section-title
          >
            {copy.t("analytics.topPages.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="gn-analytics-top-pages">
          <ErrorState error={error} onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="gn-analytics-top-pages__title">
            {copy.t("analytics.topPages.title")}
          </CardTitle>
        </CardHeader>
        <CardContent
          className={cx(
            "gn-analytics-top-pages",
            "gn-analytics-top-pages--loading",
          )}
          data-analytics-top-pages-loading
        >
          {Array.from(
            { length: 5 },
            (_, index) => `top-pages-skeleton-${index}`,
          ).map((skeletonId) => (
            <Skeleton
              key={skeletonId}
              className="gn-analytics-top-pages__row-skeleton"
              height={14}
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle
          className="gn-analytics-top-pages__title"
          data-analytics-section-title
        >
          {copy.t("analytics.topPages.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="gn-analytics-top-pages" data-analytics-top-pages>
        {safePages.length === 0 ? (
          <EmptyState title={copy.t("analytics.topPages.empty")} />
        ) : (
          <>
            <div
              className="gn-analytics-top-pages__header"
              data-analytics-top-pages-header
            >
              <span>{copy.t("analytics.topPages.columns.page")}</span>
              <span>{copy.t("analytics.topPages.columns.share")}</span>
              <span>{copy.t("analytics.topPages.columns.views")}</span>
              <span>{copy.t("analytics.dashboard.uniqueVisitors")}</span>
              <span>{copy.t("analytics.topPages.columns.averageTime")}</span>
            </div>
            {safePages.map((page) => (
              <div
                key={page.url}
                className="gn-analytics-top-pages__row"
                data-analytics-page-row
              >
                <span
                  className="gn-analytics-top-pages__url"
                  data-analytics-page-url
                >
                  {page.url}
                </span>
                <div
                  className="gn-analytics-top-pages__bar-track"
                  data-analytics-page-bar-container
                >
                  <Progress
                    className="gn-analytics-top-pages__bar"
                    value={page.views}
                    max={maxViews}
                  />
                </div>
                <span
                  className="gn-analytics-top-pages__views"
                  data-analytics-page-views
                >
                  {page.views.toLocaleString()}
                </span>
                <span
                  className="gn-analytics-top-pages__unique-visitors"
                  data-analytics-page-unique-visitors
                >
                  {page.uniqueVisitors !== undefined
                    ? page.uniqueVisitors.toLocaleString()
                    : "—"}
                </span>
                <span
                  className="gn-analytics-top-pages__average-time"
                  data-analytics-page-average-time
                >
                  {formatDuration(page)}
                </span>
              </div>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
